import { apiConfig } from "@/config/api";
import { apiFetch, UPLOAD_API_TIMEOUT_MS } from "@/lib/apiFetch";
import type { AdminLocationAssignment } from "@/services/adminService";
import type { UserJabatanAssignment } from "@/services/managementService";

export interface User {
  // Core identifiers
  id: string;
  id_client?: string;
  callsign?: string;
  
  // Personal information
  name: string;
  email: string;
  jenis_kelamin?: string;
  kewarganegaraan?: string;
  nri?: string;
  ktp?: string;
  isValidKTP?: boolean | null; // null = not validated, true = valid, false = invalid
  no_hp?: string;
  jabatan_pekerjaan?: string;
  
  // Address information
  alamat?: string;
  provinsi?: string;
  kabupaten?: string;
  kecamatan?: string;
  kelurahan?: string;
  rw?: string;
  kode_pos?: string;

  // IAR information
  iar?: string;
  tanggal_terbit?: string | null;
  masa_laku?: string | null;
  tipe_iar?: string;
  jenis_permohonan?: string;
  
  // International IAR information
  callsign_negara_asal?: string | null;
  iar_negara_asal?: string | null;
  masa_laku_negara_asal?: string | null;
  
  // Membership information
  status_callsign?: string;
  status_keanggotaan?: string;
  no_kta?: string;
  masa_laku_kta?: string;
  
  // Media
  foto?: string;
  idcardUrl?: string;
  /** When true, KTP image is only available via GET /api/auth/ktp-idcard (cookies). */
  idcardEncrypted?: boolean;
  idcardContentType?: string;

  // Application-specific fields
  isPasswordSet?: boolean;
  isSetCompleteAddress?: boolean;
  isUpdatedEmail?: boolean;
  isUpdatePhotos?: boolean;
  isFotoValid?: boolean | null;
  fotoRejectionReason?: string | null;
  userLevel?: number;
  /** 3 nasional, 2 province, 1 city — from active adminAssignments.level (see GET /me). */
  userStage?: number;
  lastSdppiSync?: string | null;
  isSilentKey?: boolean;

  /** True when user logged in using v1-only fallback (SDPPI down). */
  legacyUser?: boolean;
  
  // Timestamps
  createdAt?: string | null;
  updatedAt?: string | null;
  
  // Assignments
  adminAssignments?: AdminLocationAssignment[];
  jabatanAssignments?: UserJabatanAssignment[];
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  token?: string; // Token is now in httpOnly cookie, not returned in response
}

/** POST /api/auth/login (callsign + password or IAR) — assignments match GET /me sources */
export interface CallsignLoginResponse {
  user: {
    id: string;
    email: string;
    name: string;
    legacyUser?: boolean;
  };
  token?: string;
  paymentStatus?: {
    needPayment: boolean;
    callsign: string;
    description: string;
    amount: number;
  };
  adminAssignments?: AdminLocationAssignment[];
  jabatanAssignments?: UserJabatanAssignment[];
}

export interface CheckCallsignResponse {
  authMethod: "password" | "iar";
  callsign: string;
  iarExists: boolean;
  iarData?: any[];
  status?: string;
}

class AuthService {
  private cachedUser: User | null = null;

  /**
   * Check callsign to determine authentication method
   */
  async checkCallsign(callsign: string): Promise<CheckCallsignResponse> {
    const response = await apiFetch(`${apiConfig.baseURL}/api/auth/check-callsign`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ callsign }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Failed to check callsign" }));
      throw new Error(error.message || "Failed to check callsign");
    }

    return await response.json();
  }

  /**
   * Login user with email and password
   * Token is automatically set as httpOnly cookie by server
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await apiFetch(`${apiConfig.baseURL}/api/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Include cookies in request
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Login failed" }));
      throw new Error(error.message || "Login failed");
    }

    const data: LoginResponse = await response.json();
    
    // Cache user data
    this.cachedUser = data.user;
    
    return data;
  }

  /**
   * Login with callsign and password
   * Token is automatically set as httpOnly cookie by server
   */
  async loginWithPassword(callsign: string, password: string): Promise<CallsignLoginResponse> {
    const response = await apiFetch(`${apiConfig.baseURL}/api/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Include cookies in request
      body: JSON.stringify({ callsign, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Login failed" }));
      throw new Error(error.message || "Login failed");
    }

    const data: CallsignLoginResponse = await response.json();

    // Cache minimal user until GET /me runs (e.g. refreshUser)
    this.cachedUser = data.user as User;

    return data;
  }

  /**
   * Login with callsign and IAR
   * Token is automatically set as httpOnly cookie by server
   */
  async loginWithIAR(callsign: string, iar: string): Promise<CallsignLoginResponse> {
    const response = await apiFetch(`${apiConfig.baseURL}/api/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Include cookies in request
      body: JSON.stringify({ callsign, iar }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Login failed" }));
      throw new Error(error.message || "Login failed");
    }

    const data: CallsignLoginResponse = await response.json();

    this.cachedUser = data.user as User;

    return data;
  }

  /**
   * Logout user
   * Cookie is automatically cleared by server
   */
  async logout(): Promise<void> {
    try {
      await apiFetch(`${apiConfig.baseURL}/api/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies in request
      });
    } catch (error) {
      console.error("Logout request failed:", error);
    }
    
    // Clear cached user data
    this.cachedUser = null;
  }

  /**
   * Get current authenticated user
   * Checks authentication status via API endpoint
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await apiFetch(`${apiConfig.baseURL}/api/auth/me`, {
        method: "GET",
        credentials: "include", // Include cookies in request
      });

      if (!response.ok) {
        this.cachedUser = null;
        return null;
      }

      const data = await response.json();
      if (data.authenticated && data.user) {
        this.cachedUser = data.user;
        return data.user;
      }

      this.cachedUser = null;
      return null;
    } catch (error) {
      console.error("Error checking authentication status:", error);
      this.cachedUser = null;
      return null;
    }
  }

  /**
   * Check if user is authenticated
   * Uses cached user data or fetches from API
   */
  async isAuthenticated(): Promise<boolean> {
    if (this.cachedUser) {
      return true;
    }

    const user = await this.getCurrentUser();
    return user !== null;
  }

  /**
   * Update user profile
   */
  async updateProfile(data: {
    alamat?: string;
    ktp?: string | null;
    provinsi?: string;
    kabupaten?: string;
    kecamatan?: string;
    kelurahan?: string;
    rw?: string;
    rt?: string;
    no_hp?: string;
    kode_pos?: string;
  }): Promise<{ message: string }> {
    const response = await apiFetch(`${apiConfig.baseURL}/api/auth/update-profile`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Failed to update profile" }));
      throw new Error(error.message || "Failed to update profile");
    }

    return await response.json();
  }

  /**
   * Update user email (without OTP verification)
   */
  async updateEmail(email: string): Promise<{ message: string }> {
    const response = await apiFetch(`${apiConfig.baseURL}/api/auth/update-email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Failed to update email" }));
      throw new Error(error.message || "Failed to update email");
    }

    return await response.json();
  }

  /**
   * Get authorization headers for API requests
   * Cookies are automatically sent by browser, no Authorization header needed
   */
  getAuthHeaders(): HeadersInit {
    return {
      "Content-Type": "application/json",
    };
  }

  /**
   * Request password reset link
   */
  async requestPasswordReset(identifier: string): Promise<{
    message: string;
    outcome:
      | "email_generic"
      | "email_sent"
      | "callsign_not_found"
      | "use_iar_no_email";
  }> {
    const response = await apiFetch(`${apiConfig.baseURL}/api/auth/forgot-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ identifier }),
    });

    if (!response.ok) {
      throw new Error("Failed to request password reset");
    }

    const data = (await response.json().catch(() => ({}))) as {
      message?: string;
      outcome?: string;
    };
    const outcomeRaw = data.outcome;
    const outcome =
      outcomeRaw === "email_sent" ||
      outcomeRaw === "callsign_not_found" ||
      outcomeRaw === "use_iar_no_email" ||
      outcomeRaw === "email_generic"
        ? outcomeRaw
        : "email_generic";

    return {
      message:
        typeof data.message === "string" && data.message.length > 0
          ? data.message
          : "Permintaan telah diproses.",
      outcome,
    };
  }

  /**
   * Reset password using token
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    const response = await apiFetch(`${apiConfig.baseURL}/api/auth/reset-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ token, newPassword }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        (errorData as { error?: string; message?: string }).error ||
          (errorData as { message?: string }).message ||
          "Link reset tidak valid atau sudah kedaluwarsa."
      );
    }
  }

  /**
   * Send OTP to email for verification
   */
  async sendEmailOtp(email: string): Promise<{ success: boolean }> {
    const response = await apiFetch(`${apiConfig.baseURL}/api/auth/send-email-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Gagal mengirim OTP" }));
      throw new Error(error.message || "Gagal mengirim OTP");
    }

    return await response.json();
  }

  /**
   * Verify OTP and mark email as updated
   */
  async verifyEmailOtp(otp: string): Promise<{ success: boolean }> {
    const response = await apiFetch(`${apiConfig.baseURL}/api/auth/verify-email-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ otp }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Gagal memverifikasi OTP" }));
      throw new Error(error.message || "Gagal memverifikasi OTP");
    }

    return await response.json();
  }

  /**
   * Change password (requires current password)
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const response = await apiFetch(`${apiConfig.baseURL}/api/auth/change-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Failed to change password" }));
      throw new Error(error.message || "Failed to change password");
    }
  }

  /**
   * Upload profile photo and/or KTP photo
   */
  async uploadPhotos(fotoFile?: File, idcardFile?: File): Promise<{ message: string; isUpdatePhotos: boolean }> {
    const formData = new FormData();
    if (fotoFile) formData.append("foto", fotoFile);
    if (idcardFile) formData.append("idcardUrl", idcardFile);

    const response = await apiFetch(`${apiConfig.baseURL}/api/auth/upload-photos`, {
      method: "POST",
      credentials: "include",
      body: formData,
      timeoutMs: UPLOAD_API_TIMEOUT_MS,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Failed to upload photos" }));
      throw new Error(error.message || "Failed to upload photos");
    }

    return await response.json();
  }
}

export const authService = new AuthService();

